import Add16 from '@carbon/icons-react/es/add/16';
import ListChecked20 from '@carbon/icons-react/es/list--checked/20';
import { SecureRoute } from '@okta/okta-react';
import { useBoolean } from 'ahooks';
import React, { FC, useMemo } from 'react';
import { Helmet } from 'react-helmet';
import { FormattedMessage, useIntl } from 'react-intl';
import { Switch, useHistory } from 'react-router';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { FieldSectionType } from 'api/field-sections/types';
import { AddButton } from 'components/atoms/button';
import { PageLoading } from 'components/atoms/loading';
import { TertiaryButton } from 'components/atoms/tertiary-button';
import { ErrorBoundary } from 'components/molecules/error-boundary';
import { Page } from 'components/molecules/page';
import { HeaderWithIconTitle } from 'components/molecules/page/page';
import { FieldSectionList } from 'components/organisms/field-sections/section-list';
import { NewSectionCreation } from 'components/organisms/field-sections/sortable-section-list-components';
import {
  EditFieldPage,
  NewFieldPage,
} from 'components/organisms/module-settings/fields';
import { canShowInTaskFieldsSettings } from 'components/organisms/module-settings/fields/utils';
import { RoutePaths } from 'constant';
import { useTranslation } from 'hooks/use-translation';
import { useTasksFieldsQuery } from 'queries';
import { useFieldSectionsQuery } from 'queries/field-sections';
import { Bar } from '../styled';

const StyledPage = styled(Page)`
  ${Page.Content} {
    padding: var(--layout-01);
    margin-bottom: calc(var(--spacing-11) - var(--spacing-05));
  }
  ${Page.Header.Title} {
    flex: 1;
  }
`;

const Header = styled.span`
  padding-bottom: var(--spacing-04);
`;

const HeaderWrapper = styled.div`
  position: sticky;
  top: 0;
  z-index: 1;
  padding-bottom: var(--spacing-04);
  background-color: var(--white-100);
`;
const AddFieldWrapper = styled.div`
  position: sticky;
  top: 82px;
  z-index: 1;
  padding-bottom: var(--spacing-03);
  background-color: var(--white-100);
`;

const BarWrapper = styled(Bar)`
  margin-bottom: 0;
`;

const NewSectionButton = styled(TertiaryButton)`
  margin-right: var(--spacing-05);
`;

const PageContentWrapper = styled(Page.Content)`
  padding-top: 0 !important;
`;

export const TaskFieldsSettingsPage: FC = () => {
  const intl = useIntl();
  const title = useTranslation('task_fields');
  const editFieldTitle = useTranslation('settings.fields.task.edit');
  const newFieldTitle = useTranslation('settings.fields.task.new');
  const history = useHistory();
  const onClose = () => history.push(RoutePaths.taskFields);
  const [
    isCreatingSection,
    { setTrue: setIsCreatingSectionTrue, setFalse: setIsCreatingSectionFalse },
  ] = useBoolean(false);
  const {
    data: fieldList,
    isLoading: isFieldsLoading,
    refetch: refetchFields,
  } = useTasksFieldsQuery();

  const fieldSectionType = FieldSectionType.Task;

  const {
    data: fieldSections,
    isLoading: isFieldSectionsLoading,
    refetch: refetchFieldSection,
  } = useFieldSectionsQuery(fieldSectionType);

  const filteredFieldList = useMemo(() => {
    return fieldList ? fieldList.filter(canShowInTaskFieldsSettings) : [];
  }, [fieldList]);

  return (
    <ErrorBoundary
      onError={() => {
        refetchFields();
        refetchFieldSection();
      }}
    >
      <StyledPage>
        <Helmet>{title}</Helmet>
        <HeaderWrapper>
          <Page.Header
            $white
            title={
              <Header>
                <HeaderWithIconTitle icon={<ListChecked20 />} title={title} />
                {/* TODO:: KB_LINK. Uncomment once link is ready */}
                {/* <CTALink href={KBLinks.taskFields} target="_blank" rel="noreferrer">
              <Launch16 />
              <FormattedMessage id="settings.fields.task.header.cta" />
            </CTALink> */}
              </Header>
            }
          />
        </HeaderWrapper>
        {isFieldsLoading || isFieldSectionsLoading ? (
          <PageLoading />
        ) : (
          <PageContentWrapper>
            <AddFieldWrapper>
              <BarWrapper>
                <NewSectionButton
                  icon={<Add16 />}
                  iconPlacement="right"
                  onClick={() => setIsCreatingSectionTrue()}
                >
                  <FormattedMessage id="settings.fields.section.new" />
                </NewSectionButton>
                <Link to={RoutePaths.newTaskField}>
                  <AddButton>
                    <FormattedMessage id="settings.fields.new" />
                  </AddButton>
                </Link>
              </BarWrapper>
            </AddFieldWrapper>

            {fieldList && fieldSections ? (
              <>
                <FieldSectionList
                  fieldList={filteredFieldList}
                  fieldSections={fieldSections}
                  fieldSectionType={fieldSectionType}
                />
                {isCreatingSection && (
                  <NewSectionCreation
                    defaultSectionName={intl.formatMessage(
                      {
                        id: 'settings.fields.new.section.name',
                      },
                      {
                        sectionNumber: fieldSections.length + 1,
                      },
                    )}
                    fieldSectionType={fieldSectionType}
                    onSectionCreationSuccess={() => setIsCreatingSectionFalse()}
                    onCancel={() => setIsCreatingSectionFalse()}
                  />
                )}
              </>
            ) : null}
          </PageContentWrapper>
        )}
        <Switch>
          <SecureRoute path={RoutePaths.editTaskField}>
            <EditFieldPage
              onClose={onClose}
              objectType={FieldSectionType.Task}
              title={editFieldTitle}
            />
          </SecureRoute>
          <SecureRoute path={RoutePaths.newTaskField}>
            <NewFieldPage
              onClose={onClose}
              objectType={FieldSectionType.Task}
              title={newFieldTitle}
            />
          </SecureRoute>
        </Switch>
      </StyledPage>
    </ErrorBoundary>
  );
};
